/**
 * UI Store - small, cross-session chrome layout preferences.
 *
 * Persists independent collapse/hide toggles for navigation chrome the user may
 * want out of the way:
 *   - `siderCollapsed`   — the 80px app icon rail (left Sider) in the main shell.
 *   - `docsNavCollapsed` — the 260px documentation navigation tree on /docs (desktop).
 *
 * Uses the same Zustand `persist` pattern as tourStore / queryConsoleLayoutStore
 * so a user's chosen layout survives reloads and sessions. Both flags default to
 * `false` (expanded). They live in a single store — like queryConsoleLayoutStore,
 * which bundles several layout booleans under one key — rather than two separate
 * stores, keeping all cross-session UI chrome preferences in one place.
 */
import { create } from "zustand"
import { persist } from "zustand/middleware"

interface UiStore {
  /** When true, the 80px app icon rail (left Sider) is hidden. Default: false (expanded). */
  siderCollapsed: boolean
  /** When true, the 260px docs navigation tree is hidden on desktop. Default: false (expanded). */
  docsNavCollapsed: boolean

  /** Flip the app icon rail between shown and hidden. */
  toggleSider: () => void
  /** Explicitly set the app icon rail collapsed state. */
  setSiderCollapsed: (collapsed: boolean) => void
  /** Flip the docs navigation tree between shown and hidden. */
  toggleDocsNav: () => void
  /** Explicitly set the docs navigation tree collapsed state. */
  setDocsNavCollapsed: (collapsed: boolean) => void
}

export const useUiStore = create<UiStore>()(
  persist(
    (set) => ({
      siderCollapsed: false,
      docsNavCollapsed: false,

      toggleSider: () => {
        set((state) => ({ siderCollapsed: !state.siderCollapsed }))
      },
      setSiderCollapsed: (collapsed: boolean) => {
        set({ siderCollapsed: collapsed })
      },
      toggleDocsNav: () => {
        set((state) => ({ docsNavCollapsed: !state.docsNavCollapsed }))
      },
      setDocsNavCollapsed: (collapsed: boolean) => {
        set({ docsNavCollapsed: collapsed })
      },
    }),
    {
      name: "dataq-ui-storage",
      // Only persist the flags; the actions are recreated on each load.
      partialize: (state) => ({
        siderCollapsed: state.siderCollapsed,
        docsNavCollapsed: state.docsNavCollapsed,
      }),
    }
  )
)
